/**
 * Work marks and the labour clock — TRBUD-403.
 *
 * A work mark is "I am working on this job now": one mechanic, one job, from
 * Start work to the moment the mark ends. Decision 64 (Eden, 22 October 2026):
 * labour counts whenever the mechanic is clocked in AND marked as working on
 * the job, any day or hour. Which clock posts labour when a job leaves In bay
 * is the labour policy, set by an administrator.
 *
 * Browser-safe: zod and types only.
 */
import { z } from "zod";
import { idempotencyKey, strictObject } from "./requests.js";

/** Which clock posts labour when a job leaves In bay. */
export const labourClocks = ["stage_clock", "clocked_in_and_working"] as const;
export type LabourClock = (typeof labourClocks)[number];

/** The words the Configuration panel shows for each clock, said once. */
export const labourClockLabels: Record<LabourClock, { label: string; explanation: string }> = {
  stage_clock: {
    label: "Stage clock",
    explanation:
      "Each mechanic is charged for the part of the bay visit he was assigned to the job, inside shift hours, less pauses.",
  },
  clocked_in_and_working: {
    label: "Clocked in and working",
    explanation:
      "Each mechanic is charged only for the minutes he was clocked in and had pressed Start work on the job, any day or hour.",
  },
};

/** Why a mark ended. */
export const workMarkEndReasons = [
  "stopped",
  "ended_by_manager",
  "switched_job",
  "stoppage",
  "left_stage",
  "unassigned",
  "punched_out",
] as const;
export type WorkMarkEndReason = (typeof workMarkEndReasons)[number];

/** `POST /work-orders/:id/work-mark/start`. The signed-in person starts work. */
export const startWorkMarkBody = strictObject({ idempotencyKey });
export type StartWorkMarkBody = z.infer<typeof startWorkMarkBody>;

/**
 * `POST /work-orders/:id/work-mark/stop`. Omit `userId` to stop your own work;
 * an administrator or manager may name someone else.
 */
export const stopWorkMarkBody = strictObject({
  idempotencyKey,
  userId: z.string().uuid().optional(),
});
export type StopWorkMarkBody = z.infer<typeof stopWorkMarkBody>;

/**
 * `POST /work-orders/:id/labour-pause` and `.../labour-pause/resume`. An
 * administrator or manager pauses, or resumes, one assigned mechanic's labour
 * clock on this job only (Eden, 3 November 2026).
 */
export const labourPauseBody = strictObject({
  idempotencyKey,
  userId: z.string().uuid(),
});
export type LabourPauseBody = z.infer<typeof labourPauseBody>;

/** What pausing or resuming one mechanic's clock answers. */
export interface LabourPauseResult {
  /** True when this request changed the clock; false when it was already so. */
  changed: boolean;
}

/** `PUT /labour-policy`. Administrators only. */
export const setLabourPolicyBody = strictObject({
  labourClock: z.enum(labourClocks),
});
export type SetLabourPolicyBody = z.infer<typeof setLabourPolicyBody>;

/** What starting work answers. */
export interface StartWorkMarkResult {
  markId: string;
  /** False means the minutes will not count until he clocks in. Never a refusal. */
  clockedIn: boolean;
  /** The job whose mark this start ended, when he switched jobs. */
  switchedFromWorkOrderId: string | null;
  /**
   * Which clock posts labour now. Being clocked out only costs minutes under
   * `clocked_in_and_working`; the stage clock ignores the time clock.
   */
  labourClock: LabourClock;
}

/**
 * Roles never charged repair labour (Eden, 3 November 2026: Matt Farless only
 * manages the jobs he is assigned to). The rule the daily clock-in requirement
 * already uses: holding the mechanic role as well does not change it. The
 * automatic posting reads this list in SQL, so the two cannot disagree.
 */
export const rolesNotChargedLabour = ["admin", "manager"] as const;

/** Is a person holding these roles charged repair labour on the jobs he is assigned? */
export function chargesRepairLabour(roles: readonly string[]): boolean {
  return !roles.some((role) => (rolesNotChargedLabour as readonly string[]).includes(role));
}

/** Why a manager or administrator has no Start work, said once. */
export const MANAGERS_NOT_CHARGED_LABOUR =
  "Managers and administrators are not charged repair labour, so there is no work to start. " +
  "Log any time you spend on a job on its Time card, as Admin processing.";

/** What stopping work answers. `ended: false` means nothing was open. */
export interface StopWorkMarkResult {
  ended: boolean;
}

/** One person working on a job now, as the job header shows them. */
export interface WorkMarkWorker {
  userId: string;
  displayName: string;
  startedAt: string;
  clockedIn: boolean;
}

/** `GET /work-marks/mine`: the job the signed-in person is working on now. */
export interface MyWorkMarkResponse {
  mark: { markId: string; workOrderId: string; startedAt: string } | null;
  clockedIn: boolean;
}

/** `GET /labour-policy`. */
export interface LabourPolicyResponse {
  labourClock: LabourClock;
  updatedAt: string | null;
  updatedByLabel: string | null;
  canEdit: boolean;
}

/** The sentence shown after starting work while not clocked in. */
export const NOT_CLOCKED_IN_WARNING = "You are not clocked in, so this time will not count.";

/**
 * The warning after Start work, or null. Only true under "Clocked in and
 * working": the stage clock charges the visit whether he is clocked in or not,
 * so warning him there told him something false.
 */
export function startWorkWarning(result: Pick<StartWorkMarkResult, "clockedIn" | "labourClock">): string | null {
  return result.labourClock === "clocked_in_and_working" && !result.clockedIn ? NOT_CLOCKED_IN_WARNING : null;
}
